import React, { useCallback, useEffect, useState } from 'react';
import LotCard from '@/components/lot/LotCard';
import { Card } from '@/components/shadcn-ui/card';
import { getLotById } from '@/network/api.service';
import { CircleCheck, Pencil } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import formatDateTime from '@/lib/format-date-time';
import FermentationProcessCheckDrawer from '@/components/lot/FermentationProcessCheckDrawer';
import EndFermentationDrawer from '@/components/lot/EndFermentationDrawer';
import UpdateProcessingStepModal from '@/components/lot/UpdateProcessingStepModal';
import ProcessCheckCard from '@/components/lot/ProcessCheckCard';
import MediaGrid from '@/components/sharedComponents/MediaGrid';

const ProcessStepCompletePage = () => {
  const [stepData, setStepData] = useState();
  const [lot, setLot] = useState();
  const [editingCheck, setEditingCheck] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [endFermentationDrawerOpen, setEndFermentationDrawerOpen] = useState(false);
  const { id, step_id } = useParams();

  const fetchLot = useCallback(async () => {
    // setPageLoading(true);
    const { data } = await getLotById(id);

    if (data?.data) {
      setLot(data.data);

      const [filtered] = data.data.processing_steps?.filter(step => step_id === step._id);
      setStepData(filtered);
    }

    // setPageLoading(false);
  }, [id, step_id]);

  useEffect(() => {
    fetchLot();
  }, [fetchLot]);

  return (
    <div className="flex flex-1 flex-col space-y-[18px] px-4 py-[18px]">
      <LotCard lot={lot} />

      <div>
        {/* Title */}
        <div className="my-1 ml-2 flex items-center gap-2 text-base font-semibold">
          <div>{stepData?.sequence + '.'}</div>
          <div>{stepData?.name}</div>
        </div>

        <Card className="rounded-2xl px-4 py-5 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <div className="text-base font-semibold">Stage data</div>
              <div className="flex items-center gap-1 rounded-full bg-[#171A1F] px-3 py-0.5 text-xs font-medium text-white">
                {stepData?.status === 'Completed' && <CircleCheck className="h-3 w-3" />}
                {stepData?.status || 'Yet to start'}
              </div>
            </div>

            {stepData?.status === 'Completed' && stepData?.name === 'Fermentation' ? (
              <Link to={`/process/${id}/step/${step_id}/update`}>
                <Pencil className="h-4 w-4" />
              </Link>
            ) : (
              <UpdateProcessingStepModal stepData={stepData} lotId={id} onSuccess={fetchLot} />
            )}
          </div>

          <div className="mt-1.5 flex flex-col gap-1">
            {stepData?.name === 'Fermentation' && (
              <>
                <div className="flex items-center gap-1 text-sm">
                  <div className="font-medium">Aerobic:</div>
                  <div className="font-normal">{stepData?.data.aerobic ? 'Yes' : 'No'}</div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <div className="font-medium">Modality:</div>
                  <div className="font-normal">{stepData?.data.modality}</div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <div className="font-medium">Agent:</div>
                  <div className="font-normal">{stepData?.data.agent_type}</div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <div className="font-medium">Notes:</div>
                  <div className="font-normal">{stepData?.data.notes}</div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <div className="font-medium">Recipe:</div>
                  <div className="font-normal">{stepData?.data.recipe}</div>
                </div>
              </>
            )}

            {/* Visible for all methods */}
            <div className="flex items-center gap-1 text-sm">
              <div className="font-medium">
                {stepData?.name === 'Fermentation' ? `Fermentation start` : `Start of ${stepData?.name}`}:
              </div>
              <div className="font-normal">{formatDateTime(stepData?.data.start_at)}</div>
            </div>

            {/* Show the end time for the fermentation step if the step is Completed */}
            {stepData?.status === 'Completed' && stepData?.name === 'Fermentation' && (
              <div className="flex items-center gap-1 text-sm">
                <div className="font-medium">
                  {stepData?.name === 'Fermentation' ? `Fermentation end` : `End of ${stepData?.name}`}:
                </div>
                <div className="font-normal">{formatDateTime(stepData?.data.end_at)}</div>
              </div>
            )}

            {stepData?.name !== 'Fermentation' && (
              <>
                <div className="flex items-center gap-1 text-sm">
                  <div className="font-medium">Input weight of coffee:</div>
                  <div className="font-normal">{stepData?.data.input_weight_kg} kg</div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <div className="font-medium">Output weight of coffee:</div>
                  <div className="font-normal">{stepData?.data.output_weight_kg} kg</div>
                </div>
                {stepData?.name === 'Pulping' && (
                  <div className="flex items-center gap-1 text-sm">
                    <div className="font-medium">Weight of skin:</div>
                    <div className="font-normal">{stepData?.data.weight_of_skin_kg} kg</div>
                  </div>
                )}
                <div className="flex items-center gap-1 text-sm">
                  <div className="font-medium">End of {stepData?.name}:</div>
                  <div className="font-normal">{formatDateTime(stepData?.data.end_at)}</div>
                </div>
              </>
            )}

            {stepData?.data?.media?.length > 0 && <MediaGrid media={[...stepData?.data?.media]} remove={false} />}
          </div>
        </Card>
      </div>

      {stepData?.name === 'Fermentation' && (
        <div>
          {/* Title */}
          <div className="my-1 ml-2 flex items-center text-base font-semibold">
            Process Checks<div className="relative -top-0.5 ml-0.5">*</div>
          </div>

          {stepData?.data?.process_checks?.length > 0 && (
            <div className="my-2 space-y-2">
              {[...stepData?.data?.process_checks]?.reverse()?.map((check, idx) => (
                <ProcessCheckCard
                  key={check._id}
                  check={check}
                  idx={stepData?.data?.process_checks?.length - idx - 1}
                  setEditingCheck={setEditingCheck}
                  setDrawerOpen={setDrawerOpen}
                />
              ))}
            </div>
          )}

          {/* will add the id when the user clicks on pencil button when we show the drawer */}
          <FermentationProcessCheckDrawer
            lotId={id}
            processStepId={step_id}
            onSuccess={fetchLot}
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            checkData={editingCheck}
            onClose={() => {
              setEditingCheck(null);
            }}
          />

          {stepData?.data?.process_checks?.length > 0 && (
            <EndFermentationDrawer
              open={endFermentationDrawerOpen}
              onOpenChange={setEndFermentationDrawerOpen}
              onClose={() => setEndFermentationDrawerOpen(false)}
              lotId={id}
              processStepId={step_id}
              onSuccess={fetchLot}
              disabled={stepData?.status === 'Completed'}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProcessStepCompletePage;
